// components/legal/TermsOfService.js
import React, { useEffect } from 'react';
import styled from 'styled-components';
import Container from '../common/Container';
import { SectionTitle } from '../common/SharedStyles';
import Button from '../common/Button';
import { Link } from 'react-router-dom';

const LegalSection = styled.section`
  padding: 6rem 0;
  background-color: var(--background-primary);
`;

const LegalContainer = styled(Container)`
  max-width: 800px;
`;

const EffectiveDate = styled.p`
  font-style: italic;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  text-align: center;
`;

const IntroText = styled.p`
  margin-bottom: 2rem;
  line-height: 1.7;
`;

const SectionHeading = styled.h2`
  color: var(--accent-secondary);
  margin-top: 2.5rem;
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const CompanyInfo = styled.div`
  background-color: var(--background-secondary);
  padding: 1.5rem;
  border-radius: 5px;
  margin: 1.5rem 0;
  border: 1px solid var(--border-color);
`;

const Paragraph = styled.p`
  margin-bottom: 1rem;
  line-height: 1.7;
`;

const BulletList = styled.ul`
  margin: 1rem 0;
  padding-left: 1.5rem;

  li {
    margin-bottom: 0.75rem;
    line-height: 1.7;
  }
`;

const BackButton = styled(Button)`
  margin-top: 3rem;
`;

const TermsOfService = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalSection id="terms-of-service">
      <LegalContainer>
        <SectionTitle>Terms of Service</SectionTitle>
        <EffectiveDate>Effective Date: January 12th, 2026</EffectiveDate>

        <IntroText>
          These Terms of Service ("Terms") govern your access to and use of the software and services
          provided by Signage LLC ("Signage," "we," "our," or "us"). By accessing, subscribing to,
          downloading, or using our software, you agree to be bound by these Terms.
        </IntroText>

        <SectionHeading>1. Company Information</SectionHeading>
        <CompanyInfo>
          <p>
            Signage LLC<br />
            216 Remount Dr<br />
            San Antonio, TX 78218<br />
            Email: support@signage-llc.com
          </p>
        </CompanyInfo>

        <SectionHeading>2. Software Description</SectionHeading>
        <Paragraph>
          Signage LLC provides a desktop-only software application known as Sivo, compatible with
          Apple (macOS) and Windows operating systems. The software is made available for download
          through our website following subscription and payment.
        </Paragraph>

        <SectionHeading>3. Subscription, Trial, and Billing</SectionHeading>
        <BulletList>
          <li>Signage LLC offers a free 2-day trial at no charge.</li>
          <li>
            During the trial period, users are shown estimated and cumulative savings generated
            through use of the software.
          </li>
          <li>
            At the conclusion of the 2-day trial, users are shown the total savings achieved during
            the full trial period.
          </li>
          <li>
            Unless canceled before the end of the 2-day trial period, your payment method on file will be
            automatically charged $200 (USD) for a monthly subscription on the day immediately
            following the end of the trial.
          </li>
          <li>
            The monthly subscription renews automatically each billing cycle until canceled by the user.
          </li>
          <li>
            All payments are processed securely through Stripe. By providing payment information and
            starting the trial, you expressly authorize Signage LLC to charge applicable fees in
            accordance with these Terms.
          </li>
        </BulletList>

        <SectionHeading>4. Account Use and Restrictions</SectionHeading>
        <BulletList>
          <li>Accounts are for individual subscriber use only.</li>
          <li>
            Each organization is limited to a maximum of one (1) staff member and one (1) admin user in addition to the owner account.
          </li>
          <li>
            Reselling, sharing, or transferring accounts is strictly prohibited. (Our software
            functionality prevents shared use.)
          </li>
          <li>
            By creating an account, you agree to receive emails about product updates, features, and announcements.
          </li>
        </BulletList>

        <SectionHeading>5. Cancellation Policy</SectionHeading>
        <BulletList>
          <li>
            Users may cancel their subscription at any time through their account dashboard on the website.
          </li>
          <li>
            Subscriptions may also be canceled through the billing portal provided during checkout, which is
            managed securely through our payment processor.
          </li>
          <li>
            To avoid being charged for the next billing cycle, cancellations must be completed before the
            next billing date. Once canceled, the subscription will remain active until the end of the current
            billing period, after which no further charges will occur.
          </li>
        </BulletList>

        <SectionHeading>6. Refund Policy</SectionHeading>
        <Paragraph>
          This Refund Policy outlines the billing practices, refund guidelines, and subscription
          management terms for the software and services provided by Signage LLC ("Signage," "we,"
          "our," or "us"). By purchasing, subscribing to, or using our services, you acknowledge and agree
          to the policies described below regarding refunds, billing cycles, subscription cancellations, and
          dispute resolution.
        </Paragraph>
        <Paragraph>
          For clarity and transparency, all charges will appear on your billing statement as "Signage LLC."
          If you have any questions regarding billing, subscriptions, or charges associated with your
          account, please review the information below or contact our support team for assistance.
        </Paragraph>
        <BulletList>
          <li>
            Refunds may be issued in rare circumstances, such as billing errors, mistaken card charges, or
            verified technical issues that prevent proper use of the service.
          </li>
          <li>
            Each refund request is reviewed individually, and refunds may be granted when deemed
            reasonable and appropriate based on the circumstances.
          </li>
          <li>
            While refunds are uncommon due to the digital nature of our software, we maintain high
            standards of quality and reliability to minimize the likelihood of such issues.
          </li>
        </BulletList>

        <SectionHeading>7. Chargeback Prevention &amp; Dispute Resolution</SectionHeading>
        <Paragraph>
          We are committed to resolving any billing concerns quickly and fairly. If you have questions
          about a charge or believe you were billed incorrectly, please contact our support team before
          initiating a chargeback with your bank or credit card provider.
        </Paragraph>
        <Paragraph>
          Filing a chargeback without first contacting us may delay resolution and may result in the
          suspension or termination of your account while the dispute is under review.
        </Paragraph>
        <Paragraph>
          Our team will work with you directly to review the situation and determine the appropriate
          resolution in accordance with this Refund Policy. Most billing concerns can be resolved quickly
          through our support channels without the need for a formal dispute.
        </Paragraph>
        <Paragraph>
          By purchasing or subscribing to our services, you agree to contact us first to attempt to resolve
          any billing issue prior to initiating a chargeback.
        </Paragraph>
        <Paragraph>
          All charges will appear on your billing statement as "Signage LLC."
        </Paragraph>

        <SectionHeading>8. Fraud Prevention &amp; Transaction Review</SectionHeading>
        <Paragraph>
          To protect our customers and maintain the integrity of our platform, all transactions are subject
          to automated and manual fraud prevention screening.
        </Paragraph>
        <Paragraph>
          We reserve the right to review any order, subscription, or account activity that appears unusual,
          high-risk, or inconsistent with normal purchasing behavior. This may include verifying billing
          details, requesting additional information, or temporarily delaying service activation while the
          review is completed.
        </Paragraph>
        <Paragraph>
          If a transaction is flagged by our fraud prevention systems, we may:
        </Paragraph>
        <BulletList>
          <li>Temporarily suspend or delay access to the service</li>
          <li>Request identity or billing verification</li>
          <li>Cancel or refund the transaction if it cannot be verified</li>
        </BulletList>
        <Paragraph>
          Accounts found to be associated with fraudulent activity, unauthorized payment methods, or
          abuse of our platform may be permanently suspended or terminated.
        </Paragraph>
        <Paragraph>
          Our goal is to protect both our customers and our business from unauthorized transactions
          while ensuring legitimate users can continue using the service without interruption.
        </Paragraph>

        <SectionHeading>9. Risk Compliance</SectionHeading>
        <Paragraph>
          We are committed to maintaining a secure, compliant, and trustworthy platform for all users. Our
          services and payment systems operate in accordance with applicable financial regulations,
          payment network requirements, and industry best practices.
        </Paragraph>
        <Paragraph>
          To protect our platform and customers, we may monitor account activity, transactions, and
          usage patterns to identify potential violations of our policies, fraud, or other prohibited activities.
        </Paragraph>
        <Paragraph>
          We reserve the right to restrict, suspend, or terminate accounts that are found to be in violation
          of our Terms of Service, engaging in fraudulent behavior, abusing the platform, or attempting to
          circumvent billing systems or security safeguards.
        </Paragraph>
        <Paragraph>
          In certain circumstances, we may cooperate with payment processors, financial institutions, or
          regulatory authorities to investigate suspicious activity or ensure compliance with applicable
          laws and payment network rules.
        </Paragraph>
        <Paragraph>
          Our goal is to maintain a safe environment for legitimate users while protecting the integrity of
          our services and payment infrastructure.
        </Paragraph>

        <SectionHeading>10. Customer Clarity</SectionHeading>
        <Paragraph>
          We strive to provide clear and transparent information about our services so customers fully
          understand what they are purchasing. Our platform provides digital tools designed to assist sign
          manufacturers with estimating, sourcing materials, and calculating project costs.
        </Paragraph>
        <Paragraph>
          Before subscribing, customers are encouraged to review all available product information,
          pricing details, and policy pages to ensure the service meets their needs.
        </Paragraph>
        <Paragraph>
          By purchasing or subscribing to our services, you acknowledge that you understand the nature
          of the service being provided, including the features available within the platform, the
          subscription billing structure, and the applicable policies outlined on this website.
        </Paragraph>
        <Paragraph>
          If you have any questions regarding our services, billing, or policies, we encourage you to
          contact our support team prior to making a purchase. We are always happy to clarify any details
          and ensure you have the information needed to make an informed decision.
        </Paragraph>
        <Paragraph>
          For any questions regarding billing, subscriptions, or platform functionality, please contact our
          support team at support@signage-llc.com.
        </Paragraph>

        <SectionHeading>11. Service Availability and Termination</SectionHeading>
        <BulletList>
          <li>
            We reserve the right to suspend or terminate access to the software if you violate these Terms.
          </li>
          <li>
            We may modify, suspend, or discontinue the software or any portion of the services at any time.
          </li>
          <li>
            We also reserve the right to deny refund requests once a service has been completed or fulfilled.
          </li>
        </BulletList>

        <SectionHeading>12. Disclaimer of Warranties</SectionHeading>
        <Paragraph>
          The software is provided on an "as is" and "as available" basis. Signage LLC makes no
          warranties, express or implied, regarding the performance, reliability, or accuracy of the
          software or savings estimates displayed.
        </Paragraph>

        <SectionHeading>13. Limitation of Liability</SectionHeading>
        <Paragraph>
          To the maximum extent permitted by law, Signage LLC shall not be liable for any indirect,
          incidental, consequential, or special damages arising from the use of or inability to use
          the software.
        </Paragraph>

        <SectionHeading>14. Governing Law</SectionHeading>
        <Paragraph>
          These Terms are governed by and construed in accordance with the laws of the United States
          and the State of Texas, without regard to conflict-of-law principles.
        </Paragraph>

        <SectionHeading>15. Third-Party Names and Trademarks</SectionHeading>
        <Paragraph>
          Third-party company, supplier, product, and trade names are used solely for identification
          and compatibility purposes unless otherwise indicated. Unless a supplier is specifically
          identified as a sponsor or partner within Sivo, Sivo and Signage LLC are independent and are
          not affiliated with, sponsored by, endorsed by, or authorized by that supplier or trademark
          owner. Sponsored supplier listings may include names, logos, links, or other materials
          provided or authorized by the sponsoring supplier. All third-party trademarks and trade names
          remain the property of their respective owners.
        </Paragraph>

        <SectionHeading>16. Changes to These Terms</SectionHeading>
        <Paragraph>
          We reserve the right to update or modify these Terms at any time. Updated Terms will be
          posted on our website with a revised effective date. Continued use of the software after
          changes are posted constitutes acceptance of the updated Terms.
        </Paragraph>

        <Link to="/">
          <BackButton variant="primary">Back to Website</BackButton>
        </Link>
      </LegalContainer>
    </LegalSection>
  );
};

export default TermsOfService;
